import {
  CardTypeEnum,
  Maybe,
  NextBestActionTile,
  OnboardingCardActionResultTypenames,
  OnboardingCardResult,
  OnboardingCardResultTypenames,
  TileStatus,
  TileType,
  WalletOnboardingCard,
} from '@varo/graphql-schema-types';
import { HomeCardDependencies } from 'features/home/HomeScreen/dependencies/types';
import { OnboardingCardProps } from 'shared/components/vxl/cards/OnboardingCard/OnboardingCard';
import { WalletOnboardingCardProps } from 'shared/components/vxl/cards/OnboardingCard/WalletOnboardingCard/WalletOnboardingCard';
import { IllustrationSourceType } from 'shared/components/vxl/Illustration/Illustration';
import { OnboardingCarouselProps } from 'shared/components/vxl/OnboardingCarousel/OnboardingCarousel';
import { VXLComponent, VXLComponentId } from 'shared/renderer/types';
import { isNotNil } from 'shared/utils/isNotNil/isNotNil';

interface NextBestActionTileNode extends VXLComponent {
  id: string;
  name:
    | VXLComponentId.ONBOARDING_CARD
    | VXLComponentId.ONBOARDING_CARD_CAROUSEL
    | VXLComponentId.WALLET_ONBOARDING_CARD;
  props:
    | OnboardingCarouselProps
    | OnboardingCardProps
    | WalletOnboardingCardProps;
}

function generateWalletNextBestActionStandaloneCard(
  action: WalletOnboardingCard,
  dependencies: HomeCardDependencies,
): WalletOnboardingCardProps {
  const { refreshTiles } = dependencies;
  return {
    bodyText: action.bodyText,
    illustration: action?.illustration
      ? (action.illustration as IllustrationSourceType)
      : undefined,
    isDisabled: false,
    cardholderName: action.card?.cardHolderName?.full ?? '',
    isDebit: action.card?.type === CardTypeEnum.DebitCard,
    cardId: action.cardId,
    primaryAccountNumberSuffix: action.card?.maskedCardNumber ?? '',
    onFlowComplete: (response) => {
      if (response === 'SUCCESSFUL') {
        refreshTiles?.([TileType.NextBestActionTile]);
      }
    },
  };
}

function generateNextBestActionStandaloneCardProps(
  action: OnboardingCardResult,
  dependencies: HomeCardDependencies,
): OnboardingCardProps | WalletOnboardingCardProps | null {
  const {
    checkCardSuffix,
    checkIfWalletIsAvailable,
    initiatePinwheelForNBA,
    navigateToRoute,
  } = dependencies;

  if (
    action.__typename === OnboardingCardResultTypenames.WalletOnboardingCard
  ) {
    const actionAsWallet = action;
    const accountSuffix = actionAsWallet.card?.maskedCardNumber;
    if (!accountSuffix) {
      return null;
    }
    const hasCardBeenAdded = checkCardSuffix(accountSuffix) ?? 'UNKNOWN';
    if (
      checkIfWalletIsAvailable() ||
      hasCardBeenAdded === 'ADDED' ||
      !actionAsWallet.cardId
    ) {
      return null;
    }
    return generateWalletNextBestActionStandaloneCard(
      actionAsWallet,
      dependencies,
    );
  } else if (
    action.__typename === OnboardingCardResultTypenames.OnboardingCard
  ) {
    const actionAsOnboardingCard = action;
    return {
      bodyText: action.bodyText,
      illustration: action?.illustration
        ? (action.illustration as IllustrationSourceType)
        : undefined,
      actionText: actionAsOnboardingCard.actionText ?? '',
      isDisabled: false,
      onPress: () => {
        if (
          actionAsOnboardingCard.actionV2?.__typename ===
          OnboardingCardActionResultTypenames.RouteNavigationAction
        ) {
          navigateToRoute(actionAsOnboardingCard.actionV2);
        } else if (
          actionAsOnboardingCard.actionV2?.__typename ===
          OnboardingCardActionResultTypenames.LaunchPinwheelFlowAction
        ) {
          initiatePinwheelForNBA(actionAsOnboardingCard.actionV2);
        }
      },
    };
  }
  return null;
}

function generateNextBestActionCardCarousel(
  actions: OnboardingCardResult[],
  dependencies: HomeCardDependencies,
): OnboardingCarouselProps {
  const carouselCardProps = actions.map((action) =>
    generateNextBestActionStandaloneCardProps(action, dependencies),
  );
  return {
    onboardingCards: carouselCardProps.filter(isNotNil),
  };
}

const nextBestActionTile = (
  tile: NextBestActionTile,
  dependencies: HomeCardDependencies,
): Maybe<NextBestActionTileNode> => {
  if (tile.status !== TileStatus.Loaded) {
    return null;
  }
  const actions = tile.onboardingCardsV2;

  if (actions && actions.length > 1) {
    return {
      id: tile.id,
      name: VXLComponentId.ONBOARDING_CARD_CAROUSEL,
      props: generateNextBestActionCardCarousel(actions, dependencies),
    };
  }

  const [firstAction] = actions;

  if (!firstAction) {
    return null;
  }

  const props = generateNextBestActionStandaloneCardProps(
    firstAction,
    dependencies,
  );

  if (!props) {
    return null;
  }

  const vxlID =
    firstAction.__typename === OnboardingCardResultTypenames.OnboardingCard
      ? VXLComponentId.ONBOARDING_CARD
      : VXLComponentId.WALLET_ONBOARDING_CARD;
  return {
    id: tile.id,
    name: vxlID,
    props: props,
  };
};

export default nextBestActionTile;
